import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  TrendingUp, Truck, ListChecks, Package, AlertTriangle, Users, BellRing,
  Calendar, Search, ArrowUpRight, CheckCircle2,
} from "lucide-react";
import { formatCurrency, formatNumber, STATUS_BADGE } from "@/lib/constants";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MomStrip } from "@/components/DashboardKit";

const AXIS_STYLE = { stroke: "#94a3b8", fontSize: 11 };
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 };
const CHART_HEIGHT = { width: "100%", height: 280 };

function KpiTile({ label, value, sub, icon: Icon, accent = "slate", testid }) {
  const ACCENT = {
    slate: "bg-slate-50 text-slate-600",
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    violet: "bg-violet-50 text-violet-600",
  }[accent];
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-bold text-slate-900 mt-1.5 font-[Manrope]">{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${ACCENT}`}>
            <Icon className="h-4.5 w-4.5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function dueDateClass(t) {
  if (t.overdue) return "text-red-600 font-semibold";
  if (t.due_soon) return "text-amber-700";
  return "text-slate-700";
}

export default function SalesDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [notifiedKeys, setNotifiedKeys] = useState(() => new Set());
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/sales")
      .then((r) => mounted && setData(r.data))
      .catch(() => toast.error("Failed to load sales dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Pop-up reminders for overdue tasks + follow-ups due today — one per record per session.
  useEffect(() => {
    if (!data) return;
    const batch = [];
    for (const t of data.tasks || []) {
      if (t.overdue) batch.push({ key: `task-${t.id}`, msg: `Overdue task: ${t.task_title}`, kind: "task" });
    }
    const today = new Date().toISOString().slice(0, 10);
    for (const f of data.followups || []) {
      if (f.next_followup_date === today) batch.push({ key: `fu-${f.id}`, msg: `Follow-up today: ${f.customer}`, kind: "followup" });
    }
    const fresh = batch.filter((b) => !notifiedKeys.has(b.key)).slice(0, 5);
    if (fresh.length) {
      for (const n of fresh) {
        if (n.kind === "task") toast.warning(n.msg, { icon: "⚠️" });
        else toast.info(n.msg, { icon: "📞" });
      }
      setNotifiedKeys((prev) => {
        const next = new Set(prev);
        fresh.forEach((n) => next.add(n.key));
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const filteredOrders = useMemo(() => {
    if (!data?.orders) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.orders;
    return data.orders.filter((o) =>
      [o.customer, o.product, o.sales_person, o.sale_type, o.status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data, search]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-80" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="sales-dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Sales Dashboard</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">Hello {user?.name?.split(" ")[0]} — your sales command room</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sales value by person, customer-wise orders, dispatch status, task reminders and stock available to commit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {s.overdue_tasks > 0 && (
            <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50 gap-1" data-testid="overdue-badge">
              <AlertTriangle className="h-3 w-3" /> {s.overdue_tasks} overdue
            </Badge>
          )}
          {s.followups_due > 0 && (
            <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 gap-1" data-testid="followups-badge">
              <BellRing className="h-3 w-3" /> {s.followups_due} follow-ups
            </Badge>
          )}
        </div>
      </div>

      <MomStrip items={data.mom} />

      {/* High-level KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sales-kpi-grid">
        <KpiTile label="Total Sales Value" value={formatCurrency(s.total_value)} sub={`${s.order_count} orders`} icon={TrendingUp} accent="emerald" testid="kpi-total-sales" />
        <KpiTile label="Won" value={formatCurrency(s.won_value)} sub={`${s.customer_count} customers`} icon={CheckCircle2} accent="blue" testid="kpi-won" />
        <KpiTile label="Dispatch Pending" value={s.dispatch_pending} sub={`${s.dispatch_transit} in transit · ${s.dispatch_delivered} delivered`} icon={Truck} accent="amber" testid="kpi-dispatch-pending" />
        <KpiTile label="My Pending Tasks" value={s.pending_tasks} sub={`${s.overdue_tasks} overdue · ${s.due_soon_tasks} due soon`} icon={ListChecks} accent="rose" testid="kpi-pending-tasks" />
      </div>

      <Tabs defaultValue="sales" className="w-full" data-testid="sales-tabs">
        <TabsList className="h-auto flex-wrap bg-slate-100 p-1" data-testid="sales-tabs-list">
          <TabsTrigger value="sales" data-testid="tab-total-sales"><TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Total Sales</TabsTrigger>
          <TabsTrigger value="dispatch" data-testid="tab-dispatch"><Truck className="h-3.5 w-3.5 mr-1.5" /> Dispatch Status</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> Pending Tasks</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock"><Package className="h-3.5 w-3.5 mr-1.5" /> Production Stock</TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Total Sales ── */}
        <TabsContent value="sales" className="space-y-4" data-testid="panel-sales">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200" data-testid="salespeople-chart-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-blue-600" />
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sales by Salesperson</div>
                </div>
                <div className="text-sm text-slate-500 mb-4">Value generated by each team member</div>
                <div style={CHART_HEIGHT}>
                  <ResponsiveContainer>
                    <BarChart data={data.salespeople} layout="vertical" margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" {...AXIS_STYLE} tickFormatter={(v) => `₹${Math.round(v / 1000)}K`} />
                      <YAxis type="category" dataKey="person" {...AXIS_STYLE} width={120} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [formatCurrency(v), "Value"]} />
                      <Bar dataKey="value" fill="#0f172a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="top-customers-card">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Top Customers</div>
                <div className="text-sm text-slate-500 mb-4">By cumulative order value</div>
                <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin">
                  {data.top_customers.length === 0 && <div className="text-sm text-slate-500">No customers yet.</div>}
                  {data.top_customers.map((c) => (
                    <div key={c.customer} className="flex items-center justify-between p-2.5 border border-slate-200 rounded-md" data-testid={`customer-${c.customer}`}>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{c.customer}</div>
                        <div className="text-[11px] text-slate-500">{c.orders} order{c.orders !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="text-sm font-bold text-slate-900 font-mono">{formatCurrency(c.value)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200" data-testid="orders-table-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Customer & Order-wise Sales</div>
                  <div className="text-sm text-slate-500">{filteredOrders.length} of {data.orders.length} records</div>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input className="pl-9 w-64" placeholder="Search customer / product / rep" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="orders-search" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Customer", "Product", "Qty", "Value", "Type", "Sales Person", "Payment", "Status"].map((h) => (
                        <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                      ))}
                      <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 && (
                      <tr><td colSpan={9} className="text-center py-10 text-slate-500">No sales records.</td></tr>
                    )}
                    {filteredOrders.map((o) => (
                      <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`order-row-${o.id}`}>
                        <td className="py-2.5 px-3 font-semibold text-slate-900">{o.customer || "—"}</td>
                        <td className="py-2.5 px-3 text-slate-700">{o.product || "—"}</td>
                        <td className="py-2.5 px-3 font-mono">{formatNumber(o.quantity)}</td>
                        <td className="py-2.5 px-3 font-mono font-semibold">{formatCurrency(o.value)}</td>
                        <td className="py-2.5 px-3 text-slate-600">{o.sale_type || "—"}</td>
                        <td className="py-2.5 px-3 text-slate-700">{o.sales_person || "—"}</td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(o.payment_status || "")}>{o.payment_status || "—"}</Badge></td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(o.status || "")}>{o.status || "—"}</Badge></td>
                        <td className="py-2.5 px-3 text-right">
                          <Link to={`/m/sales/${o.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`order-open-${o.id}`}>
                            Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {data.followups.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/40" data-testid="followups-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <BellRing className="h-4 w-4 text-amber-600" />
                  <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Upcoming Follow-ups (next 7 days)</div>
                </div>
                <div className="divide-y divide-amber-100">
                  {data.followups.map((f) => (
                    <Link key={f.id} to={`/m/sales_conversion/${f.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-between py-2 hover:bg-amber-100/40 px-1 rounded" data-testid={`followup-${f.id}`}>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{f.customer || "—"} <span className="text-slate-500 font-normal">· {f.product_interest || f.product || ""}</span></div>
                        <div className="text-xs text-slate-600">
                          <Calendar className="h-3 w-3 inline mr-1" /> {f.next_followup_date}
                          {f.overdue && <span className="ml-2 text-red-600 font-semibold">OVERDUE</span>}
                          {f.sales_person && <span className="ml-2 text-slate-500">· {f.sales_person}</span>}
                        </div>
                      </div>
                      <Badge variant="outline" className={STATUS_BADGE(f.stage)}>{f.stage}</Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab 2: Dispatch Status ── */}
        <TabsContent value="dispatch" className="space-y-4" data-testid="panel-dispatch">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Dispatch Pipeline</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["PO No.", "Product", "Total", "Dispatched", "Pending", "Invoice", "Transport", "Delivery", "Payment"].map((h) => (
                        <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                      ))}
                      <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.dispatches.length === 0 && (
                      <tr><td colSpan={10} className="text-center py-10 text-slate-500">No dispatch records.</td></tr>
                    )}
                    {data.dispatches.map((d) => (
                      <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`dispatch-row-${d.id}`}>
                        <td className="py-2.5 px-3 font-mono font-semibold">{d.po_number || "—"}</td>
                        <td className="py-2.5 px-3">{d.product || "—"}</td>
                        <td className="py-2.5 px-3 font-mono">{formatNumber(d.total_qty)}</td>
                        <td className="py-2.5 px-3 font-mono">{formatNumber(d.dispatched_qty)}</td>
                        <td className="py-2.5 px-3 font-mono text-amber-700">{formatNumber(d.pending_qty)}</td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(d.invoice_status || "")}>{d.invoice_status || "—"}</Badge></td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(d.transport_status || "")}>{d.transport_status || "—"}</Badge></td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(d.delivery_status || "")}>{d.delivery_status || "—"}</Badge></td>
                        <td className="py-2.5 px-3">{d.payment_pending === "Yes" ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Pending</Badge> : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">OK</Badge>}</td>
                        <td className="py-2.5 px-3 text-right">
                          <Link to={`/m/dispatch/${d.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`dispatch-open-${d.id}`}>
                            Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 3: Pending Task ── */}
        <TabsContent value="tasks" className="space-y-4" data-testid="panel-tasks">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Tasks</div>
                  <div className="text-sm text-slate-500">Pending / due / overdue — reminders fire on dashboard load.</div>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50">{s.overdue_tasks} overdue</Badge>
                  <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{s.due_soon_tasks} due in 7d</Badge>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Task", "Assigned To", "Assigned By", "Priority", "Due", "Status"].map((h) => (
                        <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                      ))}
                      <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tasks.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-500">All caught up — no pending tasks.</td></tr>
                    )}
                    {data.tasks.map((t) => (
                      <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${t.overdue ? "bg-red-50/40" : ""}`} data-testid={`task-row-${t.id}`}>
                        <td className="py-2.5 px-3 font-semibold text-slate-900">{t.task_title || "—"}</td>
                        <td className="py-2.5 px-3 text-slate-700">{t.assigned_to || "—"}</td>
                        <td className="py-2.5 px-3 text-slate-600">{t.assigned_by || "—"}</td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.priority || "")}>{t.priority || "—"}</Badge></td>
                        <td className="py-2.5 px-3">
                          <span className={dueDateClass(t)}>
                            {t.due_date || "—"}{t.overdue && " · OVERDUE"}
                          </span>
                        </td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.status)}>{t.status}</Badge></td>
                        <td className="py-2.5 px-3 text-right">
                          <Link to={`/m/tasks/${t.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`task-open-${t.id}`}>
                            Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 4: Production Stock ── */}
        <TabsContent value="stock" className="space-y-4" data-testid="panel-stock">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Stock available to commit</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Product", "Model", "Category", "Current Stock", "Dispatch Pending", "Production Pending", "Available", "Status"].map((h) => (
                        <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                      ))}
                      <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.stocks.length === 0 && (
                      <tr><td colSpan={9} className="text-center py-10 text-slate-500">No products in catalogue.</td></tr>
                    )}
                    {data.stocks.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`stock-row-${p.id}`}>
                        <td className="py-2.5 px-3 font-semibold text-slate-900">{p.product_name || "—"}</td>
                        <td className="py-2.5 px-3 font-mono">{p.model_no || "—"}</td>
                        <td className="py-2.5 px-3 text-slate-600">{p.category || "—"}</td>
                        <td className="py-2.5 px-3 font-mono">{formatNumber(p.current_stock)}</td>
                        <td className="py-2.5 px-3 font-mono text-amber-700">{formatNumber(p.dispatch_pending)}</td>
                        <td className="py-2.5 px-3 font-mono">{formatNumber(p.production_pending)}</td>
                        <td className={`py-2.5 px-3 font-mono font-bold ${p.available > 0 ? "text-emerald-700" : "text-red-600"}`}>{formatNumber(p.available)}</td>
                        <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(p.status || "")}>{p.status || "—"}</Badge></td>
                        <td className="py-2.5 px-3 text-right">
                          <Link to={`/m/products/${p.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`stock-open-${p.id}`}>
                            Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
